import React, { useState, useEffect } from 'react';
import { addOrUpdatePortForwardingConfig, AddPortForwardingConfigPayload, getPortForwardingConfigs, PortForwardingConfig, getGatewayDeviceId, checkIsDeviceConnectedByDeviceId } from '../../Services/serviceURL';
import { toast } from 'react-toastify';
import { usePagination } from '../../hooks/usePagination';
import searchIcon from '../../Images/search-icon.svg';
import infoIcon from '../../Images/info-circle_orange.png';
import './RemoteAccess.css';

interface RemoteAccessProps {
  selectedAssetRecordId: string | null;
}

const RemoteAccess: React.FC<RemoteAccessProps> = ({ selectedAssetRecordId }) => {
  // State for port forwarding configurations
  const [portForwardingConfigs, setPortForwardingConfigs] = useState<PortForwardingConfig[]>([]);
  const [loadingPortConfigs, setLoadingPortConfigs] = useState<boolean>(false);
  const [remoteSearchTerm, setRemoteSearchTerm] = useState<string>('');
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // State for port forwarding modal
  const [showAddPortModal, setShowAddPortModal] = useState<boolean>(false);
  const [showAccessModal, setShowAccessModal] = useState<boolean>(false);
  const [selectedConfig, setSelectedConfig] = useState<PortForwardingConfig | null>(null);
  const [isDeviceConnected, setIsDeviceConnected] = useState<boolean>(false);
  const [checkingConnection, setCheckingConnection] = useState<boolean>(false);
  
  // State for collapsible sections in details modal
  const [isBasicDetailsExpanded, setIsBasicDetailsExpanded] = useState<boolean>(false);
  const [isAccessDetailsExpanded, setIsAccessDetailsExpanded] = useState<boolean>(true);
  
  const [portFormData, setPortFormData] = useState({
    serviceName: '',
    inputPort: '6001', // Default to 6001 
    targetPort: '80',
    targetIpAddress: '192.168.0.5',
    protocol: 'TCP',
    accessLimit: '60', // Default to 60 minutes (first option)
    tls: true // Default TLS to checked
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState({
    serviceName: '',
    inputPort: '',
    targetPort: '',
    targetIpAddress: ''
  });

  // API function to fetch port forwarding configurations
  const fetchPortForwardingConfigs = async (assetId: string) => {
    if (!assetId) return;
    
    setLoadingPortConfigs(true);
    try {
      const configs = await getPortForwardingConfigs(assetId);
      setPortForwardingConfigs(configs);
    } catch (error: any) {
      console.error('Error fetching port forwarding configs:', error);
      setPortForwardingConfigs([]);
    } finally {
      setLoadingPortConfigs(false);
    }
  };

  // Effect to fetch data when selectedAssetRecordId changes
  useEffect(() => {
    if (selectedAssetRecordId) {
      fetchPortForwardingConfigs(selectedAssetRecordId);
    }
  }, [selectedAssetRecordId]);

  // Effect to periodically check and update expiration status
  useEffect(() => {
    // Only set up interval if we have configs
    if (portForwardingConfigs.length === 0) return;

    // Check expiration status every 10 seconds to update UI
    const intervalId = setInterval(() => {
      // Force re-render by creating a new array reference
      // This will cause the isConfigExpired function to be re-evaluated with current time
      setPortForwardingConfigs(prev => {
        // Create a new array to trigger re-render
        return prev.map(config => ({ ...config }));
      });
    }, 10000); // Check every 10 seconds

    return () => clearInterval(intervalId);
  }, [portForwardingConfigs.length]);

  // Modal handlers for port forwarding
  const handleAddPortForwardingClick = () => {
    setShowAddPortModal(true);
  };

  const handleClosePortModal = () => {
    setShowAddPortModal(false);
    setIsSaving(false);
    setFieldErrors({
      serviceName: '',
      inputPort: '',
      targetPort: '',
      targetIpAddress: ''
    });
    setPortFormData({
      serviceName: '',
      inputPort: '',
      targetPort: '80',
      targetIpAddress: '192.168.0.5',
      protocol: 'TCP',
      accessLimit: '15', // Default to 15 minutes (first option)
      tls: true // Default TLS to checked
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setPortFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSavePortForwarding = async () => {
    if (!selectedAssetRecordId) {
      toast.error('No device ID available for saving port forwarding config');
      return;
    }

    // Validate required fields
    const errors = {
      serviceName: '',
      inputPort: '',
      targetPort: '',
      targetIpAddress: ''
    };

    // Skip serviceName validation since the field is commented out
    //   errors.serviceName = 'Please fill required field';
    if (!portFormData.inputPort) {
      errors.inputPort = 'Please fill required field';
    }
    if (!portFormData.targetPort) {
      errors.targetPort = 'Please fill required field';
    }
    if (!portFormData.targetIpAddress) {
      errors.targetIpAddress = 'Please fill required field';
    }

    // Check if any field has errors (excluding serviceName)
    if (errors.inputPort || errors.targetPort || errors.targetIpAddress) {
      setFieldErrors(errors);
      return;
    }

    // Clear field errors if all required fields are filled
    setFieldErrors({
      serviceName: '',
      inputPort: '',
      targetPort: '',
      targetIpAddress: ''
    });

    // Validate port numbers
    const inputPort = parseInt(portFormData.inputPort);
    const targetPort = parseInt(portFormData.targetPort);
    
    // Validate input port (must be 4-digit non-reserved port)
    if (isNaN(inputPort) || inputPort < 1000 || inputPort > 9999) {
      toast.error('Input port must be a 4-digit number (1000-9999)');
      return;
    }
    
    // Check for common reserved ports (comprehensive list)
    const reservedPorts = [
      // System ports (1000-1099)
      1000, 1001, 1002, 1003, 1004, 1005, 1006, 1007, 1008, 1009, 1010,
      1011, 1012, 1013, 1014, 1015, 1016, 1017, 1018, 1019, 1020, 1021, 1022, 1023, 1024,
      // Database ports
      3306, 5432, 1433, 27017, 6379, 5984,
      // Remote access ports
      3389, 5900, 5901, 5902,
      // Web server ports (common ones that might conflict)
      8080, 8443, 8000, 8008, 8888,
      // Development ports
      3000, 4000, 5000, 8001, 8002, 8003, 8004, 8005, 8006, 8007, 8009, 8010,
      // Other common reserved ports
      2049, 2082, 2083, 2086, 2087, 2095, 2096
    ];
    
    if (reservedPorts.includes(inputPort)) {
      toast.error(`Input port ${inputPort} is reserved by the system. Please try ports in 6000-6999 or 7000-7999 range.`);
      return;
    }
    
    // Validate target port
    if (isNaN(targetPort) || targetPort < 1 || targetPort > 65535) {
      toast.error('Target port must be a valid port number (1-65535)');
      return;
    }
    
    // Validate IP address format (more comprehensive)
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(portFormData.targetIpAddress)) {
      toast.error('Please enter a valid IP address (e.g., 192.168.1.100)');
      return;
    }
    
    // Additional IP validation - check each octet
    const ipParts = portFormData.targetIpAddress.split('.');
    for (let i = 0; i < ipParts.length; i++) {
      const octet = parseInt(ipParts[i]);
      if (octet < 0 || octet > 255) {
        toast.error('IP address octets must be between 0 and 255');
        return;
      }
    }
    
    // Validate target IP is not localhost or broadcast
    if (portFormData.targetIpAddress === '127.0.0.1' || 
        portFormData.targetIpAddress === '0.0.0.0' ||
        portFormData.targetIpAddress === '255.255.255.255') {
      toast.error('Target IP address cannot be localhost, broadcast, or null address');
      return;
    }

    // Clear any previous errors and set loading state
    setIsSaving(true);

    try {
      // First, get the gateway device ID from asset details
      const gatewayDeviceId = await getGatewayDeviceId(selectedAssetRecordId);
      
      if (!gatewayDeviceId) {
        toast.error('Gateway Device ID not found for this asset');
        setIsSaving(false);
        return;
      }

      // Convert access limit from minutes to seconds
      const accessLimitInMinutes = portFormData.accessLimit ? parseInt(portFormData.accessLimit) : 60;
      const accessLimitInSeconds = accessLimitInMinutes * 60;

      // Prepare the payload for the API with the new structure
      const configPayload: AddPortForwardingConfigPayload = {
        id: '', // Use empty string for new configs
        serviceName: portFormData.serviceName.trim() || 'Remote Access', // Default service name since field is commented out
        inputPort: parseInt(portFormData.inputPort),
        targetPort: parseInt(portFormData.targetPort),
        targetIpAddress: portFormData.targetIpAddress.trim(),
        protocol: portFormData.protocol.toUpperCase(),
        accessLimit: accessLimitInSeconds, // Send access limit in seconds
        tls: portFormData.tls,
        deviceId: gatewayDeviceId,
        currentUserIpAddress: '' // Send as empty string as requested
      };

      // Call the API
      const response = await addOrUpdatePortForwardingConfig(configPayload);
      
      // Check if the response indicates success
      if (response && response.Success === false) {
        // Handle API failure response
        const errorMessage = response.Message || response.ErrorMessage || 'Operation failed';
        console.error(' Port forwarding config save failed:', errorMessage);
        toast.error(`Failed to save port forwarding configuration: ${errorMessage}`);
        setIsSaving(false);
        return;
      }
      
      // For successful HTTP responses, check if the config was actually saved
      // If the response contains Data.success = false but no error message, 
      // it might still be successful - let's check by refreshing the data
      if (response && response.Success === true) {
        
        // Log the ID returned from the API response
        if (response.Data?.id) {
        }
        
        // Check if Data.success is false AND we have a meaningful error message
        let hasWarning = false;
        if (response.Data?.success === false && response.Data?.errorMessage) {
          let errorMessage = response.Data.errorMessage;
          
          // Check if errorMessage is a JSON string and parse it
          try {
            const parsedError = JSON.parse(errorMessage);
            if (parsedError.error) {
              errorMessage = parsedError.error;
            }
          } catch (e) {
            // If parsing fails, use the original message
          }
          
          console.warn(' Port forwarding config saved but with warnings:', errorMessage);
          
          // Check if this is a field validation error
          if (errorMessage.includes('Field Validation Error:')) {
            toast.warning(errorMessage);
          } else {
            toast.warning(`Configuration saved with warning: ${errorMessage}`);
          }
          hasWarning = true;
        }
        
        // Show success message only if there are no warnings
        if (!hasWarning) {
          toast.success('Remote access requested successfully');
         handleClosePortModal();
        }
        
         
        
        // Refresh the configs to see if it was actually saved
        if (selectedAssetRecordId) {
          await fetchPortForwardingConfigs(selectedAssetRecordId);
        }
        
        return; // Exit successfully
      }
      
    } catch (error) {
      console.error(' Error saving port forwarding config:', error);
      
      // Check if it's an axios error with response data
      const axiosError = error as any;
      if (axiosError.response?.data?.Message) {
        // Check if this is a field validation error that should be shown as warning
        const errorMessage = axiosError.response.data.Message;
        if (errorMessage.includes('Field Validation Error:')) {
          // Show field validation errors as warning instead of error
          console.warn('Port forwarding config saved but with warnings:', errorMessage);
          toast.warning(errorMessage);
          
          // Still close modal and refresh data for field validation warnings
          if (selectedAssetRecordId) {
            await fetchPortForwardingConfigs(selectedAssetRecordId);
          }
        } else {
          // Use the specific error message from the API for actual errors
          const errorMsg = `Failed to save port forwarding configuration: ${errorMessage}`;
          toast.error(errorMsg);
        }
      } else if (axiosError.response?.data?.message) {
        // Alternative message property
        const errorMessage = axiosError.response.data.message;
        if (errorMessage.includes('Field Validation Error:')) {
          // Show field validation errors as warning instead of error
          console.warn('Port forwarding config saved but with warnings:', errorMessage);
          toast.warning(errorMessage);
          
          // Still close modal and refresh data for field validation warnings
          if (selectedAssetRecordId) {
            await fetchPortForwardingConfigs(selectedAssetRecordId);
          }
        } else {
          const errorMsg = `Failed to save port forwarding configuration: ${errorMessage}`;
          toast.error(errorMsg);
        }
      } else if (axiosError.response?.status) {
        // HTTP status error
        const errorMsg = `Failed to save port forwarding configuration: HTTP ${axiosError.response.status} - ${axiosError.response.statusText || 'Unknown error'}`;
        toast.error(errorMsg);
      } else {
        // Generic error message
        const errorMsg = 'Failed to save port forwarding configuration. Please try again.';
        toast.error(errorMsg);
      }
    } finally {
      // Always reset loading state
      setIsSaving(false);
    }
  };

  // Filter port forwarding configs based on search term and exclude incomplete configs
  const filteredPortConfigs = portForwardingConfigs.filter(config => {
    // First filter out configs with empty service names (incomplete configs)
    if (!config.serviceName || config.serviceName.trim() === '') {
      return false;
    }
    
    // Then apply search filter
    if (!remoteSearchTerm) return true;
    const searchLower = remoteSearchTerm.toLowerCase();
    return (
      config.serviceName?.toLowerCase().includes(searchLower) ||
      config.userName?.toLowerCase().includes(searchLower) ||
      config.targetIpAddress?.toLowerCase().includes(searchLower) ||
      config.protocol?.toLowerCase().includes(searchLower) ||
      config.inputPort?.toString().includes(searchLower) ||
      config.targetPort?.toString().includes(searchLower)
    );
  });

  // Use pagination hook
  const {
    currentPage,
    totalPages,
    currentData: paginatedConfigs,
    totalItems,
    goToPage,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    data: filteredPortConfigs,
    itemsPerPage: itemsPerPage,
  });

  // Helper functions for formatting
  const formatAccessLimit = (accessLimit?: number, duration?: number): string => {
    if (!accessLimit || accessLimit === 0) return 'Unlimited';
    
    // Convert seconds to minutes for display (API now returns accessLimit in seconds)
    const minutes = Math.floor(accessLimit / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        return `${days} day${days > 1 ? 's' : ''} ${remainingHours} hour${remainingHours > 1 ? 's' : ''}`;
      } else {
        return `${days} day${days > 1 ? 's' : ''}`;
      }
    } else if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
      }
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  const formatTLS = (tls?: boolean): string => {
    return tls ? 'Yes' : '';
  };

  //Helper function to format date/time for display
// Helper function to format date/time for display
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date

    // Format as a more readable date string
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString || '-'; // Return original string if parsing fails
  }
};

  // Helper function to check if a config is actually expired
  const isConfigExpired = (config: PortForwardingConfig): boolean => {
    // Priority 1: Check expiredTime if available (most reliable)
    if (config.expiredTime !== null && config.expiredTime !== undefined) {
      try {
        const currentTime = Date.now();
        let expiredTimeMs: number;
        
        if (typeof config.expiredTime === 'number') {
          // If it's a number, check if it's in seconds or milliseconds
          // Timestamps after year 2000 in milliseconds are > 946684800000
          // If it's less than this, assume it's in seconds
          expiredTimeMs = config.expiredTime < 946684800000 
            ? config.expiredTime * 1000 
            : config.expiredTime;
        } else {
          // Try to parse as date string
          expiredTimeMs = new Date(config.expiredTime).getTime();
        }
        
        if (!isNaN(expiredTimeMs)) {
          const isExpired = currentTime >= expiredTimeMs;
          return isExpired;
        }
      } catch (e) {
        console.error('Error parsing expiredTime:', e);
      }
    }
    
    // Priority 2: Calculate from createdTime + accessLimit
    if (config.createdTime !== null && config.createdTime !== undefined && config.accessLimit) {
      try {
        let createdTimeMs: number;
        
        if (typeof config.createdTime === 'number') {
          // If it's a number, check if it's in seconds or milliseconds
          createdTimeMs = config.createdTime < 946684800000 
            ? config.createdTime * 1000 
            : config.createdTime;
        } else {
          // Try to parse as date string
          createdTimeMs = new Date(config.createdTime).getTime();
        }
        
        if (!isNaN(createdTimeMs) && config.accessLimit > 0) {
          const accessLimitMs = config.accessLimit * 1000; // accessLimit is in seconds
          const expirationTime = createdTimeMs + accessLimitMs;
          const currentTime = Date.now();
          const isExpired = currentTime >= expirationTime;
          return isExpired;
        }
      } catch (e) {
        console.error('Error calculating from createdTime:', e, config);
      }
    }
    
    // Priority 2b: Calculate from createdOn + accessLimit (alternative field name)
    if (config.createdOn && config.accessLimit) {
      try {
        const createdOnMs = new Date(config.createdOn).getTime();
        if (!isNaN(createdOnMs) && config.accessLimit > 0) {
          const accessLimitMs = config.accessLimit * 1000; // accessLimit is in seconds
          const expirationTime = createdOnMs + accessLimitMs;
          const currentTime = Date.now();
          const isExpired = currentTime >= expirationTime;
          return isExpired;
        }
      } catch (e) {
        console.error('Error calculating from createdOn:', e, config);
      }
    }
    
    // Priority 3: Calculate from requestedTime + accessLimit
    if (config.requestedTime && config.accessLimit) {
      try {
        const requestedTimeMs = new Date(config.requestedTime).getTime();
        if (!isNaN(requestedTimeMs) && config.accessLimit > 0) {
          const accessLimitMs = config.accessLimit * 1000; // accessLimit is in seconds
          const expirationTime = requestedTimeMs + accessLimitMs;
          const currentTime = Date.now();
          const isExpired = currentTime >= expirationTime;
          return isExpired;
        }
      } catch (e) {
        console.error('Error calculating from requestedTime:', e);
      }
    }
    
    // Priority 4: If we have timestamps but calculation failed, check expired field as fallback
    // But prioritize calculation over API's expired field since API might have stale data
    
    // Priority 5: Check expired field from API only if we couldn't calculate from timestamps
    if (config.expired === true) {
      return true;
    }
    
    // Priority 6: If expired is explicitly false but NO timestamps to verify, trust API
    // BUT if we have timestamps and expired=false, we already calculated above, so trust calculation
    if (config.expired === false && !config.createdTime && !config.createdOn && !config.requestedTime && !config.expiredTime) {
      return false;
    }
    
    // Priority 7: If we couldn't calculate and expired is null/undefined/false with timestamps
    // This means timestamps exist but calculation failed - assume expired for safety
    const hasTimestamps = config.createdTime || config.createdOn || config.requestedTime || config.expiredTime;
    if (hasTimestamps) {
      console.warn('Timestamps exist but calculation failed - defaulting to expired for safety', config);
      return true; // If we have timestamps but can't calculate, better to be safe
    }
    
    // Priority 8: No timestamps, no expired field - check if config looks old
    const hasBasicInfo = config.serviceName || config.inputPort || config.id;
    if (hasBasicInfo) {
      // Config exists but no expiration info - assume expired to be safe
      console.warn('Config has no expiration info - defaulting to expired for safety');
      return true;
    }
    
    return false;
  };

  const getActionButtonClass = (config: PortForwardingConfig): string => {
    const expired = isConfigExpired(config);
    return `action-btn ${expired ? 'expired' : 'active'}`;
  };

  const getActionButtonText = (config: PortForwardingConfig): string => {
    const expired = isConfigExpired(config);
    return expired ? 'Expired' : 'Active';
  };

  const handleActiveNavigation = (config: PortForwardingConfig) => {
    // Build HTTP/HTTPS URL from config data
    const hostname = config.hostname || config.endpoint;
    const port = config.port; // Use config.port instead of config.inputPort for the external URL
    
    if (hostname && port) {
      // Use HTTPS if TLS is enabled, otherwise HTTP
      const protocol = config.tls ? 'https' : 'http';
      const url = `${protocol}://${hostname}:${port}`;
      
      
      // Open in new tab/window
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Unable to navigate: Hostname or port not available');
    }
  };

  const handleInfoIconClick = (configId: string) => {
    
    // Handle empty or invalid config IDs
    if (!configId || configId.trim() === '') {
      console.error('Invalid config ID:', configId);
      toast.error('Invalid configuration ID');
      return;
    }
    
    // Find the config from the current local state
    const config = portForwardingConfigs.find(c => c.id === configId);
    
    if (!config) {
      console.error('Config not found for ID:', configId);
      toast.error('Configuration not found');
      return;
    }

    // Directly set the config and open modal without connection checking
    setSelectedConfig(config);
    setIsDeviceConnected(true); // Assume connected for info display
    setShowAccessModal(true);
  };

  const handleAccessButtonClick = async (configId: string) => {
    
    // Handle empty or invalid config IDs
    if (!configId || configId.trim() === '') {
      console.error('Invalid config ID:', configId);
      toast.error('Invalid configuration ID');
      return;
    }
    
    try {
      // Since GetPortforwardingConfigById endpoint doesn't exist (404),
      // we'll find the config from the current local state instead
      const config = portForwardingConfigs.find(c => c.id === configId);
      
      if (!config) {
        console.error('Config not found for ID:', configId);
        toast.error('Configuration not found');
        return;
      }

      setSelectedConfig(config);
      setCheckingConnection(true);
      
      // First get the gateway device ID for this asset
      if (selectedAssetRecordId) {
        const gatewayDeviceId = await getGatewayDeviceId(selectedAssetRecordId);
        
        if (gatewayDeviceId) {
          // Check if device is connected
          const connectionStatus = await checkIsDeviceConnectedByDeviceId(gatewayDeviceId);
          setIsDeviceConnected(connectionStatus);
        } else {
          console.warn('Gateway Device ID not found');
          setIsDeviceConnected(false);
        }
      }
    } catch (error) {
      console.error('Error checking device connection:', error);
      setIsDeviceConnected(false);
      toast.error('Failed to load configuration details');
    } finally {
      setCheckingConnection(false);
      setShowAccessModal(true);
    }
  };

  const handleCloseAccessModal = () => {
    setShowAccessModal(false);
    setSelectedConfig(null);
    setIsDeviceConnected(false);
    setCheckingConnection(false);
  };

  return (
    <div className="tab-content remote-tab-content">
      <div className="remote-control-container">
        {/* Main Content */}
        <div className="remote-main-content">
          {/* Controls */}
          <div className="remote-controls">
            <div className="remote-search-container">
              <input 
                type="text" 
                placeholder="Filter By Keyword" 
                className="remote-search-input"
                value={remoteSearchTerm}
                onChange={(e) => setRemoteSearchTerm(e.target.value)}
              />
              <img src={searchIcon} alt="Search" className="remote-search-icon" />
            </div>
            <div className="remote-actions">
              <button className="remote-add-btn" onClick={handleAddPortForwardingClick}>
                + Request Remote Access
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="remote-table-container slim-scroll">
            <table className="remote-table">
              <thead>
                <tr>
                  <th>User </th>
                  <th>Input Port </th>
                  <th>Target Port</th>
                  <th>Target IP Address </th>
                  <th>Protocol</th>
                  <th>Access Limit</th>
                  <th>TLS </th>
                  <th>Created Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loadingPortConfigs ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                      Loading Port Forwarding Configurations...
                    </td>
                  </tr>
                ) : filteredPortConfigs.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#a0a0a0' }}>
                      {portForwardingConfigs.length === 0 
                        ? 'No Port Forwarding Configurations Found'
                        : 'No Configurations Match Your Search Criteria'
                      }
                    </td>
                  </tr>
                ) : (
                  paginatedConfigs.map((config, index) => (
                    <tr key={config.id || `config-${index}`}>
                      <td>{config.userName || config.createdBy || 'Unknown'}</td>
                      <td>{config.inputPort || config.port || '-'}</td>
                      <td>{config.targetPort || '-'}</td>
                      <td>{config.targetIpAddress || '-'}</td>
                      <td>{config.protocol?.toUpperCase() || 'TCP'}</td>
                      <td>{formatAccessLimit(config.accessLimit || 0, config.duration || 0)}</td>
                      <td>{formatTLS(config.tls || false)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '33px' }}>
                          {(() => {
                            const expired = isConfigExpired(config);
                            return (
                              <>
                                <span 
                                  className={`status-text ${expired ? 'disabled' : 'clickable'}`}
                                  style={{ 
                                    cursor: expired ? 'default' : 'pointer',
                                    color: expired ? '#999' : '#007bff'
                                  }}
                                  onClick={() => !expired && handleActiveNavigation(config)}
                                  title={!expired ? 'Click to navigate to Soroccom login' : ''}
                                >
                                  {checkingConnection && selectedConfig?.id === config.id 
                                    ? 'Checking...' 
                                    : getActionButtonText(config)
                                  }
                                </span>
                                {!expired && (
                                  <img 
                                    src={infoIcon} 
                                    alt="Info" 
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', marginBottom:"-2px" }}
                                    onClick={() => config.id && handleInfoIconClick(config.id)}
                                    title="View remote access details"
                                  />
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="remote-pagination">
            <button 
              className="pagination-btn" 
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
               <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
               </svg> Previous
            </button>
            
            <div className="pagination-center">
              <span className="pagination-info">
                {totalItems > 0 ? (
                  <>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    {remoteSearchTerm && ` (filtered from ${portForwardingConfigs.length} total)`}
                  </>
                ) : (
                  'No entries to show'
                )}
              </span> 
              <span className="pagination-page">Page {currentPage} of {totalPages }</span>

            </div>

            <button 
              className="pagination-btn" 
              onClick={goToNextPage}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
             </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Add Port Forwarding Modal */}
      {showAddPortModal && (
        <div className="modal-overlay">
          <div className="port-forward-modal">
            <div className="modal-header">
              <h3>Request Remote Access</h3>
              <button className="modal-close" onClick={handleClosePortModal}>×</button>
            </div>
            <div className="modal-content slim-scroll">

                <div className="form-group">
                  <label>Input port <span style={{ color: '#ff4757' }}>*</span><span style={{ 
                                             color: '#999', 
                                             fontSize: '12px', 
                                             marginLeft: '4px',
                                             fontWeight: 'normal'
                                          }}>
                                          (Range:6000-8000)
                                        </span></label>
                  <input
                    type="text"
                    name="inputPort"
                    value={portFormData.inputPort}
                    onChange={handleInputChange}
                    placeholder="e.g. 6000-8000"
                  />
                  {fieldErrors.inputPort && (
                    <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.inputPort}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Target port <span style={{ color: '#ff4757' }}>*</span></label>
                  <input
                    type="text"
                    name="targetPort"
                    value={portFormData.targetPort}
                    onChange={handleInputChange}
                    placeholder="e.g. 8080"
                  />
                  {fieldErrors.targetPort && (
                    <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.targetPort}
                    </small>
                  )}
                </div>

              <div className="form-group">
                <label>Target IP address <span style={{ color: '#ff4757' }}>*</span></label>
                <input
                  type="text"
                  name="targetIpAddress"
                  value={portFormData.targetIpAddress}
                  onChange={handleInputChange}
                  placeholder="e.g. 192.168.1.100"
                />
                {fieldErrors.targetIpAddress && (
                  <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {fieldErrors.targetIpAddress}
                  </small>
                )}
              </div>

                <div className="form-group">
                  <label>Protocol</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="protocol"
                        value="TCP"
                        checked={portFormData.protocol === 'TCP'}
                        onChange={handleInputChange}
                      />
                      TCP
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="protocol"
                        value="UDP"
                        checked={portFormData.protocol === 'UDP'}
                        onChange={handleInputChange}
                      />
                      UDP
                    </label>
                  </div>
                <div className="form-group">
                  <label>Access limit </label>
                  <select
                    name="accessLimit"
                    value={portFormData.accessLimit}
                    onChange={handleInputChange}
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 Hour</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="tls"
                      checked={portFormData.tls}
                      onChange={handleInputChange}
                    />
                    TLS
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleClosePortModal}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-save" 
                onClick={handleSavePortForwarding}
                disabled={isSaving}
              >
                {isSaving ? 'Requesting...' : 'Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* On Demand Access Modal */}
      {showAccessModal && selectedConfig && (
        <div className="modal-overlay" onClick={handleCloseAccessModal}>
          <div className="access-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h1></h1>
              <button className="modal-close" onClick={handleCloseAccessModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="access-details">

{/* Common Access Details Section */}
                <div className="collapsible-section">
                  <div 
                    className="section-header-remote" 
                    onClick={() => setIsAccessDetailsExpanded(!isAccessDetailsExpanded)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0',borderBottom: '1px solid #4a5568', marginBottom: '16px' }}
                  >
                    <span className="section-title" style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Common Access Details</span>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      style={{ 
                        transform: isAccessDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {isAccessDetailsExpanded && (
                    <div className="section-content">
                      <div className="detail-row">
                        <span className="detail-label">SSH</span>
                        <span className="detail-value">
                          {(selectedConfig.hostname || selectedConfig.endpoint)
                            ? `ssh -p ${selectedConfig.inputPort || 'PORT'} ${selectedConfig.hostname || selectedConfig.endpoint} -l user`
                            : 'Hostname not available'
                          }
                        </span>
                        <button 
                          className="copy-btn" 
                          onClick={() => (selectedConfig.hostname || selectedConfig.endpoint) && navigator.clipboard.writeText(`ssh -p ${selectedConfig.inputPort || 'PORT'} ${selectedConfig.hostname || selectedConfig.endpoint} -l user`)}
                          disabled={!(selectedConfig.hostname || selectedConfig.endpoint)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>



                      <div className="detail-row">
                        <span className="detail-label">HTTPS</span>
                        <span className="detail-value">
                          {(selectedConfig.hostname || selectedConfig.endpoint) && selectedConfig.port
                            ? (
                                <a 
                                  href={`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#007bff', textDecoration: 'underline' }}
                                >
                                  {`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`}
                                </a>
                              )
                            : 'Hostname or port not available'
                          }
                        </span>
                        <button 
                          className="copy-btn" 
                          onClick={() => (selectedConfig.hostname || selectedConfig.endpoint) && selectedConfig.port && navigator.clipboard.writeText(`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`)}
                          disabled={!(selectedConfig.hostname || selectedConfig.endpoint) || !selectedConfig.port}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Details Section */}
                <div className="collapsible-section">
                  <div 
                    className="section-header-remote" 
                    onClick={() => setIsBasicDetailsExpanded(!isBasicDetailsExpanded)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #4a5568', marginBottom: '16px' }}
                  >
                    <span className="section-title" style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Additional Details</span>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      style={{ 
                        transform: isBasicDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {isBasicDetailsExpanded && (
                    <div className="section-content">
                      <div className="detail-row">
                        <span className="detail-label">SIM ID</span>
                        <span className="detail-value">{selectedConfig.simId || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.simId || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Service</span>
                        <span className="detail-value">{selectedConfig.destinationStrategy || 'IMSI_PRIORITIZED'}</span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Port</span>
                        <span className="detail-value">{selectedConfig.inputPort}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.inputPort?.toString() || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">TLS</span>
                        <span className="detail-value">{selectedConfig.tls ? 'Yes' : 'No'}</span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Hostname</span>
                        <span className="detail-value">{selectedConfig.hostname || selectedConfig.endpoint || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.hostname || selectedConfig.endpoint || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">IP address</span>
                        <span className="detail-value">{selectedConfig.targetIpAddress || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.targetIpAddress || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Access from</span>
                        <span className="detail-value">
                          {(() => {
                            const accessFrom = selectedConfig.sourceIPAddress || selectedConfig.accessFrom;
                            if (!accessFrom || accessFrom === '0.0.0.0') return 'Anywhere';
                            return accessFrom;
                          })()}
                        </span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.sourceIPAddress || selectedConfig.accessFrom || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoteAccess;